import { supabase } from './supabase';

export interface GenerateAgreementParams {
  estimateId: string;
  brandId: string;
  clientName: string;
  clientType: string;
  services: any;
  totals: any;
  discounts?: any;
}

export interface Agreement {
  id: string;
  estimate_id: string;
  proposal_response_id: string | null;
  brand_id: string;
  agreement_content: string;
  agreement_html: string | null;
  generation_method: string;
  claude_model: string | null;
  claude_prompt: string | null;
  claude_response_metadata: any;
  knowledge_base_ids: string[];
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  signed_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export async function generateAgreementWithClaude(params: GenerateAgreementParams): Promise<{ success: boolean; agreement?: Agreement; error?: string }> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    // Get brand info
    const { data: brand } = await supabase
      .from('brands')
      .select('name')
      .eq('id', params.brandId)
      .single();

    if (!brand) {
      throw new Error('Brand not found');
    }

    // Determine contract type based on services
    const isOneTimeProject = params.services.selectedWebProduct !== undefined &&
                            params.services.selectedWebProduct !== null;

    const contractType = isOneTimeProject ? 'development_project' : 'ongoing_services';

    // Build variables for contract generation
    const variables = buildContractVariables(params, brand.name, contractType);

    // Determine required clauses
    const requiredClauses = determineRequiredClauses(params.services, contractType);

    // Load knowledge base content
    const knowledgeBasePath = contractType === 'development_project'
      ? '/knowledge_base/development_project_knowledge_base.md'
      : '/knowledge_base/ongoing_services_knowledge_base.md';

    let knowledgeBaseContent = '';
    try {
      const kbResponse = await fetch(knowledgeBasePath);
      if (kbResponse.ok) {
        knowledgeBaseContent = await kbResponse.text();
      }
    } catch (error) {
      console.warn('Failed to load knowledge base file, will try edge function without it', error);
    }

    // Call edge function
    const apiUrl = `${supabaseUrl}/functions/v1/generate-agreement`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        estimateId: params.estimateId,
        brandId: params.brandId,
        contractType,
        variables,
        requiredClauses,
        knowledgeBasePath,
        knowledgeBaseContent
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to generate agreement');
    }

    return {
      success: true,
      agreement: result.agreement,
    };
  } catch (error) {
    console.error('Error generating agreement:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

function buildContractVariables(params: GenerateAgreementParams, brandName: string, contractType: string): Record<string, any> {
  const BRAND_INFO: Record<string, any> = {
    'Solved Puzzle': {
      legal_name: 'Solved Puzzle Agency LLC',
      address: '990 Biscayne Blvd #503, Miami, FL 33132',
      email: 'info@solvedpuzzle.com',
      phone: '(305) 487-7477',
    },
    'Web Design Miami Company': {
      legal_name: 'Web Design Miami LLC',
      address: '400 NW 26th St, Miami, FL 33127',
      email: 'info@webdesignmiamicompany.com',
      phone: '(786) 957-3455',
    },
    'West Palm Beach Web Design Company': {
      legal_name: 'West Palm Beach Web Design LLC',
      address: '700 S Rosemary Ave #204, West Palm Beach, FL 33401',
      email: 'info@westpalmbeachwebdesigncompany.com',
      phone: '(561) 363-3628',
    }
  };

  const brandInfo = BRAND_INFO[brandName] || BRAND_INFO['Solved Puzzle'];
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const variables: Record<string, any> = {
    brand_name: brandName,
    company_legal_name: brandInfo.legal_name,
    company_address: brandInfo.address,
    company_email: brandInfo.email,
    company_phone: brandInfo.phone,
    client_name: params.clientName,
    client_company: params.clientName,
    client_title: 'Authorized Representative',
    effective_date: today,
    signature_date: today,
  };

  // Add financial details
  if (params.totals) {
    if (contractType === 'development_project') {
      variables.total_project_cost = params.totals.grandTotal || params.totals.total_due || params.totals.web_total || 0;
      variables.original_project_cost = params.totals.web_original || variables.total_project_cost;
    } else {
      // For ongoing services, calculate total monthly investment
      const monthlyMarketing = params.totals.marketing_monthly || 0;
      const webMonthly = params.totals.web_monthly || 0;
      variables.total_monthly_investment = monthlyMarketing + webMonthly;
      variables.original_monthly_cost = variables.total_monthly_investment;

      // Add marketing breakdown if available
      if (monthlyMarketing > 0) {
        variables.marketing_monthly_fee = monthlyMarketing;
      }
      if (webMonthly > 0) {
        variables.website_monthly_fee = webMonthly;
      }
    }

    // Setup fees
    const setupFee = params.totals.setupFee || params.totals.marketing_setup || 0;
    variables.total_setup_fees = `$${setupFee.toFixed(2)}`;
    if (setupFee > 0) {
      variables.setup_fee_status = `Standard setup fees apply: $${setupFee.toFixed(2)}`;
    } else {
      variables.setup_fee_status = 'No setup fees apply to selected services';
    }

    // Add discount information
    const marketingDiscount = params.totals.marketing_discount || 0;
    const webDiscount = params.totals.web_discount || 0;
    if (marketingDiscount > 0 || webDiscount > 0) {
      variables.total_discount = marketingDiscount + webDiscount;
      variables.monthly_savings = marketingDiscount;
    }
  }

  // Build detailed service summary
  const services = [];
  const serviceDetails = [];

  if (params.services.includeWeb) {
    services.push('Website Development');
    if (params.services.selectedWebProduct) {
      serviceDetails.push(`Website: ${params.services.selectedWebProduct}`);
    }
  }

  if (params.services.includeMarketing) {
    services.push('Digital Marketing');

    // Add ad platform details
    if (params.services.selectedAdPlatforms && params.services.selectedAdPlatforms.length > 0) {
      params.services.selectedAdPlatforms.forEach((platform: string) => {
        const platformName = platform.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        serviceDetails.push(`${platformName} Management`);
      });
    }

    // Add SEO details
    if (params.services.seoTier) {
      serviceDetails.push(`SEO (${params.services.seoTier})`);
    }

    // Add Email/SMS details
    if (params.services.emailTier) {
      serviceDetails.push(`Email & SMS Marketing (${params.services.emailTier})`);
    }

    // Add CRO details
    if (params.services.croTier) {
      serviceDetails.push(`Conversion Rate Optimization (${params.services.croTier})`);
    }
  }

  variables.service_summary = services.join(', ') || 'Selected Services';
  variables.selected_services_list = serviceDetails.length > 0
    ? serviceDetails.map(s => `  • ${s}`).join('\n')
    : services.join('\n- ');

  // Add discount info if available
  if (params.discounts) {
    variables.discount_description = `Promotional discount applied`;
  }

  // DEBUG: Log all calculated variables
  console.log('=== CONTRACT VARIABLES DEBUG ===');
  console.log('Contract Type:', contractType);
  console.log('Client Name:', params.clientName);
  console.log('Services Config:', JSON.stringify(params.services, null, 2));
  console.log('Totals:', JSON.stringify(params.totals, null, 2));
  console.log('\nCalculated Variables:');
  console.log('  - total_monthly_investment:', variables.total_monthly_investment);
  console.log('  - marketing_monthly_fee:', variables.marketing_monthly_fee);
  console.log('  - website_monthly_fee:', variables.website_monthly_fee);
  console.log('  - total_setup_fees:', variables.total_setup_fees);
  console.log('  - setup_fee_status:', variables.setup_fee_status);
  console.log('  - service_summary:', variables.service_summary);
  console.log('  - selected_services_list:', variables.selected_services_list);
  console.log('  - discount variables:', {
    discount_description: variables.discount_description,
    monthly_savings: variables.monthly_savings,
    total_discount: variables.total_discount
  });
  console.log('================================');

  return variables;
}

function determineRequiredClauses(services: any, contractType: string): string[] {
  const clauses: string[] = [];

  if (contractType === 'development_project') {
    clauses.push(
      'parties_and_project_scope',
      'project_payment_terms',
      'client_content_and_cooperation',
      'intellectual_property_ownership',
      'revision_and_change_management',
      'technical_specifications_and_limitations',
      'post_launch_support_and_warranty',
      'project_termination_and_cancellation',
      'limitation_of_liability',
      'governing_law_and_dispute_resolution',
      'general_provisions',
      'execution_and_signatures'
    );

    // Add project-specific clause
    if (services.selectedWebProduct) {
      if (services.selectedWebProduct.includes('WordPress')) {
        clauses.splice(1, 0, 'wordpress_development_project_terms');
      } else if (services.selectedWebProduct.includes('Shopify')) {
        clauses.splice(1, 0, 'shopify_development_project_terms');
      } else if (services.selectedWebProduct.includes('Landing')) {
        clauses.splice(1, 0, 'landing_page_development_terms');
      }
    }
  } else {
    clauses.push(
      'parties_and_service_agreement',
      'monthly_payment_terms',
      'discount_and_promotional_terms',
      'performance_and_results_disclaimers',
      'limitation_of_liability',
      'termination_provisions',
      'client_cooperation_requirements',
      'intellectual_property_and_confidentiality',
      'governing_law_and_dispute_resolution',
      'general_provisions',
      'execution_and_signatures'
    );

    // Add service-specific clauses for ad platforms
    if (services.selectedAdPlatforms && services.selectedAdPlatforms.length > 0) {
      services.selectedAdPlatforms.forEach((platform: string) => {
        if (platform === 'google_ads') {
          clauses.splice(3, 0, 'google_ads_management_service_terms');
        } else if (platform === 'meta_ads') {
          clauses.splice(3, 0, 'meta_ads_management_service_terms');
        } else if (platform === 'tiktok_ads') {
          clauses.splice(3, 0, 'tiktok_ads_management_service_terms');
        } else if (platform === 'linkedin_ads') {
          clauses.splice(3, 0, 'linkedin_ads_management_service_terms');
        }
      });
      // Add third-party platform disclaimers
      clauses.splice(4, 0, 'third_party_platform_disclaimers');
    }

    // Add SEO service clauses
    if (services.seoTier) {
      const tierKey = services.seoTier.toLowerCase().replace(/\s+/g, '_');
      clauses.splice(3, 0, `seo_${tierKey}_service_terms`);
      clauses.splice(4, 0, 'seo_performance_disclaimers');
    }

    // Add Email/SMS service clauses
    if (services.emailTier) {
      const tierKey = services.emailTier.toLowerCase().replace(/\s+/g, '_');
      clauses.splice(3, 0, `email_sms_${tierKey}_service_terms`);
      clauses.splice(4, 0, 'email_marketing_platform_dependencies');
    }

    // Add CRO service clauses
    if (services.croTier) {
      const tierKey = services.croTier.toLowerCase().replace(/\s+/g, '_');
      clauses.splice(3, 0, `cro_${tierKey}_service_terms`);
    }

    // Add website care clauses if applicable
    if (services.pkg && services.includeWeb && !services.selectedWebProduct) {
      clauses.splice(3, 0, `website_care_${services.pkg}_service_terms`);
      clauses.splice(4, 0, 'website_technical_limitations');
    }
  }

  return clauses;
}

export async function getAgreementByEstimateId(estimateId: string): Promise<Agreement | null> {
  try {
    const { data, error } = await supabase
      .from('agreements')
      .select('*')
      .eq('estimate_id', estimateId)
      .order('created_at', { ascending: false })
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching agreement:', error);
    return null;
  }
}

export async function getAgreementsByBrand(brandId: string): Promise<Agreement[]> {
  try {
    const { data, error } = await supabase
      .from('agreements')
      .select('*')
      .eq('brand_id', brandId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching agreements:', error);
    return [];
  }
}

export async function updateAgreementStatus(agreementId: string, status: string, reviewNotes?: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const updateData: any = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (status === 'approved' && user) {
      updateData.reviewed_by = user.id;
      updateData.reviewed_at = new Date().toISOString();
      if (reviewNotes) {
        updateData.review_notes = reviewNotes;
      }
    }

    if (status === 'signed') {
      updateData.signed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('agreements')
      .update(updateData)
      .eq('id', agreementId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating agreement status:', error);
    return false;
  }
}

export async function updateAgreementContent(agreementId: string, content: string, reason: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data: agreement, error: fetchError } = await supabase
      .from('agreements')
      .select('agreement_content')
      .eq('id', agreementId)
      .maybeSingle();

    if (fetchError || !agreement) {
      throw new Error('Agreement not found');
    }

    const { error: modError } = await supabase
      .from('agreement_modifications')
      .insert({
        agreement_id: agreementId,
        modified_by: user.id,
        modification_type: 'manual_edit',
        section_modified: 'full_content',
        original_content: agreement.agreement_content,
        modified_content: content,
        reason: reason,
      });

    if (modError) throw modError;

    const { error: updateError } = await supabase
      .from('agreements')
      .update({
        agreement_content: content,
        agreement_html: convertToHTML(content),
        updated_at: new Date().toISOString(),
      })
      .eq('id', agreementId);

    if (updateError) throw updateError;
    return true;
  } catch (error) {
    console.error('Error updating agreement content:', error);
    return false;
  }
}

export async function linkAgreementToProposal(agreementId: string, proposalResponseId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('agreements')
      .update({
        proposal_response_id: proposalResponseId,
        status: 'signed',
        signed_at: new Date().toISOString(),
      })
      .eq('id', agreementId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error linking agreement to proposal:', error);
    return false;
  }
}

export async function getClaudeApiUsageStats(brandId: string, days: number = 30): Promise<any> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('claude_api_usage')
      .select('*')
      .eq('brand_id', brandId)
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    const stats = {
      totalRequests: data?.length || 0,
      successfulRequests: data?.filter(r => r.success).length || 0,
      failedRequests: data?.filter(r => !r.success).length || 0,
      totalTokens: data?.reduce((sum, r) => sum + (r.total_tokens || 0), 0) || 0,
      totalCost: data?.reduce((sum, r) => sum + (parseFloat(r.estimated_cost) || 0), 0) || 0,
      averageResponseTime: data?.length
        ? (data.reduce((sum, r) => sum + (r.response_time_ms || 0), 0) / data.length)
        : 0,
    };

    return stats;
  } catch (error) {
    console.error('Error fetching API usage stats:', error);
    return null;
  }
}

function convertToHTML(markdownText: string): string {
  let html = markdownText
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hlu])/gm, '<p>')
    .replace(/(?<![>])$/gm, '</p>');

  html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');

  return html;
}
